import React from 'react';
import { useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
  userAbbreviatedName,
} from '../../util/data';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { isUserAuthorized } from '../../util/userHelpers';

import { avatarImageVariants, hasUsableAvatarImage } from '../../util/avatarImage';
import { navAvatarImage, navAvatarSource } from '../../util/listingCardPhoto';

import { ResponsiveImage, IconBannedUser, NamedLink } from '../../components/';
import { AvailableNowDot } from '../AvailableNowBadge/AvailableNowBadge';
import { HeartAvatarFace } from '../HeartAvatar/HeartAvatar';

import css from './Avatar.module.css';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object?} props.user API entity
 * @param {string} props.renderSizes
 * @param {boolean} props.disableProfileLink
 * @param {{ name: string, params?: Object }} [props.profileRoute] public profile destination
 * @returns {JSX.Element} search icon
 */
export const Avatar = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    user,
    listing,
    image,
    renderSizes = AVATAR_SIZES,
    disableProfileLink,
    profileRoute,
    online = false,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);
  // I.e. the status is active, not pending-approval or banned
  const isUnauthorizedUser = userIsCurrentUser && !isUserAuthorized(user);
  const variant =
    user?.attributes?.state === 'pendingApproval'
      ? PROFILE_PAGE_PENDING_APPROVAL_VARIANT
      : user?.attributes?.state;

  const isBannedUser = avatarUser.attributes.banned;
  const isDeletedUser = avatarUser.attributes.deleted;

  const defaultUserDisplayName = isBannedUser
    ? intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })
    : isDeletedUser
    ? intl.formatMessage({ id: 'Avatar.deletedUserDisplayName' })
    : '';

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const avatarImage =
    image || navAvatarImage({ user: avatarUser, listing }) || avatarUser.profileImage;
  const profileImageVariants = avatarImageVariants(avatarImage);
  const hasProfileImage = hasUsableAvatarImage(avatarImage);
  const avatarSource = image ? 'profile' : navAvatarSource({ user: avatarUser, listing });
  const rootProps = {
    className: classes,
    title: displayName,
    ...(listing || image
      ? {
          'data-nav-avatar': hasProfileImage ? 'photo' : 'initials',
          'data-nav-avatar-source': avatarSource,
        }
      : {}),
  };
  const linkProps =
    profileRoute?.name
      ? { name: profileRoute.name, params: profileRoute.params || {} }
      : isUnauthorizedUser && avatarUser.id
      ? {
          name: 'ProfilePageVariant',
          params: { id: avatarUser.id.uuid, variant },
        }
      : avatarUser.id
      ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid } }
      : { name: 'ProfileBasePage' };
  const profileLinkEnabled = !disableProfileLink;
  // Initials are kept only as the accessible name; the heart is decorative.
  const heartFace = (
    <>
      <HeartAvatarFace user={avatarUser} listener={listing ? true : undefined} />
      <span className={css.visuallyHidden}>{abbreviatedName || displayName}</span>
    </>
  );

  if (isBannedUser || isDeletedUser) {
    return (
      <div {...rootProps}>
        <IconBannedUser className={css.bannedUserIcon} />
      </div>
    );
  } else if (hasProfileImage && profileLinkEnabled) {
    return (
      <NamedLink {...rootProps} {...linkProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarImage}
          variants={profileImageVariants}
          sizes={renderSizes}
        />
        <AvailableNowDot online={online} className={css.presenceDot} />
      </NamedLink>
    );
  } else if (hasProfileImage) {
    return (
      <div {...rootProps}>
        <ResponsiveImage
          rootClassName={css.avatarImage}
          alt={displayName}
          image={avatarImage}
          variants={profileImageVariants}
          sizes={renderSizes}
        />
        <AvailableNowDot online={online} className={css.presenceDot} />
      </div>
    );
  } else if (profileLinkEnabled) {
    // No photo: the member's chosen heart (official ear-heart on their circle)
    return (
      <NamedLink {...rootProps} {...linkProps}>
        {heartFace}
        <AvailableNowDot online={online} className={css.presenceDot} />
      </NamedLink>
    );
  } else {
    // No photo: the member's chosen heart (official ear-heart on their circle)
    return (
      <div {...rootProps}>
        {heartFace}
        <AvailableNowDot online={online} className={css.presenceDot} />
      </div>
    );
  }
};

export default Avatar;

export const AvatarSmall = props => (
  <Avatar
    rootClassName={css.smallAvatar}
    initialsClassName={css.initialsSmall}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium = props => (
  <Avatar
    rootClassName={css.mediumAvatar}
    initialsClassName={css.initialsMedium}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
  <Avatar
    rootClassName={css.largeAvatar}
    initialsClassName={css.initialsLarge}
    renderSizes={AVATAR_SIZES_LARGE}
    {...props}
  />
);
AvatarLarge.displayName = 'AvatarLarge';
